// Shared field catalog for the accessory ("secondary") therapy views, used both by the new-style
// stack (src/pages-new/patient/tabs/therapy/SecondaryTherapyDetail.tsx) and the legacy stack
// (src/components/templates/TherapyDialogs/SecondaryTherapyCardTabs.tsx).
//
// Field visibility/labels/order are normally driven by the PSP config
// (patientDetailConfiguration.secondaryTherapiesConfiguration.dataSchema, configured via the
// PSP configurator's "Terapia accessoria" tab). SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA below is
// the fallback used ONLY where no dataSchema prop is passed (the standalone /therapies page,
// which is not tied to a specific patient's patientDetailConfiguration).
//
// Keep prop/value/group in sync with patientSecondaryTherapyOptions in
// Internal Portals/ia-web-components/src/react-components/PspConfiguratorModal/CONSTANTS.ts.

export interface SecondaryTherapyDataSchemaElement {
  name: string
  value: string
  type?: string
  group?: string
}

export const SECONDARY_THERAPY_GROUPS: {key: string; label: string}[] = [
  {key: 'frequency', label: 'Frequenza'},
  {key: 'dosages', label: 'Dosaggi'},
  {key: 'administration', label: 'Modalità di assunzione'},
  {key: 'notes', label: 'Note'},
]

export const SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA: SecondaryTherapyDataSchemaElement[] = [
  {name: 'Data inizio terapia', value: 'startDate', type: 'date', group: 'frequency'},
  {name: 'Data fine terapia', value: 'endDate', type: 'date', group: 'frequency'},
  {name: 'Assunzione al bisogno', value: 'frequency.asNeeded', type: 'boolean', group: 'frequency'},
  {name: 'Frequenza assunzioni', value: 'frequency.times', type: 'frequencyTimes', group: 'frequency'},
  {name: 'Da ripetere nei giorni', value: 'frequency.days', type: 'frequencyDays', group: 'frequency'},
  {name: 'Orario prima assunzione', value: 'frequency.notificationTime', type: 'time', group: 'frequency'},
  {name: 'Giorni di tolleranza', value: 'frequency.toleranceDays', type: 'valueAndUnitType', group: 'frequency'},
  {name: 'Dosaggio per assunzione', value: 'assumptionDosage', type: 'assumptionDosage', group: 'dosages'},
  {name: 'Velocità infusionale', value: 'assumptionDosage.infusionRate', type: 'valueAndUnitType', group: 'dosages'},
  {name: 'Dosaggio secondario', value: 'assumptionDosage.secondaryDosage', type: 'valueAndUnitType', group: 'dosages'},
  {name: 'Diluizione (ml)', value: 'assumptionDosage.dilution', group: 'dosages'},
  {name: 'Dosaggio singolo', value: 'drugDosage', type: 'valueAndUnitType', group: 'dosages'},
  {name: 'Dosaggio settimanale', value: 'weeklyDosage', type: 'valueAndUnitType', group: 'dosages'},
  {name: 'Dosaggio mensile', value: 'monthlyDosage', type: 'valueAndUnitType', group: 'dosages'},
  {name: 'Dosaggio giornaliero', value: 'dailyDosage', type: 'valueAndUnitType', group: 'dosages'},
  {name: 'Dosaggio fiala', value: 'vialDosage', type: 'valueAndUnitType', group: 'dosages'},
  {name: 'Criterio di calcolo', value: 'calculationCriterion', type: 'valueAndUnitType', group: 'dosages'},
  {name: 'Via di assunzione', value: 'modeOfAssumption', type: 'modeOfAssumption', group: 'administration'},
  {name: 'Dispositivo', value: 'deviceOfAssumption', type: 'device', group: 'administration'},
  {name: 'Velocità device', value: 'speedOfDevice', type: 'valueAndUnitType', group: 'administration'},
  {name: 'Note', value: 'notes', type: 'text-area', group: 'notes'},
]

// Fields the configurator offered before it started saving `knownFields` next to the dataSchema.
// A field missing from both the dataSchema and `knownFields` (or from this list, for a config saved
// before `knownFields` existed) was added to the configurator after the PSP was configured: the PSP
// never chose to hide it, so it stays visible until the PSP is saved again.
const LEGACY_KNOWN_FIELDS = SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA
  .map(({value}) => value)
  .filter((value) => !['startDate', 'endDate'].includes(value))

export interface SecondaryTherapiesConfiguration {
  dataSchema?: SecondaryTherapyDataSchemaElement[]
  knownFields?: string[]
}

export function resolveSecondaryTherapyDataSchema(
  configuration?: SecondaryTherapiesConfiguration
): SecondaryTherapyDataSchemaElement[] | undefined {
  const dataSchema = configuration?.dataSchema
  if (!dataSchema?.length) return dataSchema
  const knownFields = configuration?.knownFields ?? LEGACY_KNOWN_FIELDS
  const unconfiguredFields = SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA.filter(({value}) =>
    !knownFields.includes(value) && !dataSchema.some((element) => element.value === value))
  return [...unconfiguredFields, ...dataSchema]
}

// The 4 assumptionDosage-related fields must be excluded from the "dosages" group whenever the
// therapy carries customDosages (the dosage varies per-assumption, so a single value doesn't
// apply) — mirrors the legacy `!customDosages ? [...] : []` branching.
export const ASSUMPTION_DOSAGE_SUB_FIELDS = [
  'assumptionDosage',
  'assumptionDosage.infusionRate',
  'assumptionDosage.secondaryDosage',
  'assumptionDosage.dilution',
]

// --- Reminder status --------------------------------------------------------------------------
//
// `reminderStatus` drives whether the assumption reminder goes out at all — same field, same
// four-value enum, on BOTH `therapies` and `secondary-therapies` (therapy-service generalized the
// reminder engine across the two collections, see reminderResources.js). Only one value means
// "the patient is being reminded": EXPIRED (past endDate), DISABLED (switched off explicitly) and
// INACTIVE all mean no reminder is sent. The portal only ever needs that yes/no, so the
// distinction is collapsed here rather than in each stack — the two portals must not disagree on
// what the bell means, and neither may primary/secondary therapies.

export const REMINDER_STATUSES = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  EXPIRED: 'EXPIRED',
  DISABLED: 'DISABLED'
} as const

export function isReminderActive(therapy?: Record<string, any>): boolean {
  return therapy?.reminderStatus === REMINDER_STATUSES.ACTIVE
}

export function formatReminderStatus(therapy?: Record<string, any>): string {
  return isReminderActive(therapy) ?
    'Promemoria di assunzione attivo' :
    'Promemoria di assunzione disattivato'
}

// --- Permissions ------------------------------------------------------------------------------
//
// therapy-service specs/2026-10-secondary-therapy-authz.md (RF8/RF9): what the logged-in user may
// do on a secondary therapy is decided by the backend and sent along with each therapy as
// `permissions` — the portal only reads it, it never re-implements the rules. A therapy entered by
// the centre lets the patient manage just the reminder. Without `permissions` (backend not yet
// updated) everything stays allowed, as before: the backend still answers 403 when it has to.

export type SecondaryTherapyAction = 'update' | 'updateReminder' | 'delete'

export function canOnSecondaryTherapy(therapy: Record<string, any> | undefined, action: SecondaryTherapyAction): boolean {
  return therapy?.permissions?.[action] !== false
}

export const SECONDARY_THERAPY_LOCKED_TOOLTIP = 'Terapia inserita dal centro: puoi gestire solo il promemoria'

export const SECONDARY_THERAPY_FORBIDDEN_MESSAGE =
  'Questa terapia accessoria è stata inserita dal centro: per modificarla contatta la Centrale Operativa di Italiassistenza'

// The message for a failed write: the backend's 403 gets its own explanation, anything else the
// caller's generic one.
export function secondaryTherapyErrorMessage(statusCode: number | undefined, fallback: string): string {
  return statusCode === 403 ? SECONDARY_THERAPY_FORBIDDEN_MESSAGE : fallback
}
